import {
  BookOpen,
  BrainCog,
  CheckIcon,
  Home,
  LucideIcon,
  Shield,
  Smartphone,
  Star,
} from "lucide-react";

import stage1AppOnly from "../../assets/images/stage1AppOnly.webp";
import stage1Nettle from "../../assets/images/stage1Nettle.webp";
import { NettleLogo } from "../../assets/logos/nettleLogo";
import { ProductTypes } from "../../constants/purchaseFlow";
import { usePrePurchaseURLParams } from "../../hooks/usePrePurchaseURLParams";
import { cn } from "../../utils/styling";
import { SamphireLogo } from "../ui/logos/samphireLogo";

export function ProductOption({
  variant,
  productUrlParam,
}: {
  variant: ProductTypes;
  productUrlParam: ProductTypes;
}) {
  const { setURLParam } = usePrePurchaseURLParams();

  const handleProductOptionPress = () => {
    if (productUrlParam === variant) {
      setURLParam("product", null);
    } else {
      setURLParam("product", variant);
    }
  };

  return (
    <button
      onClick={handleProductOptionPress}
      className={cn(
        "relative min-w-[200px] min-h-[80px] overflow-hidden bg-canvas-base box-border flex flex-row rounded-2xl border-border border-1",
        productUrlParam === variant &&
          "border-brand-primary bg-surface-one shadow-lg",
      )}
    >
      <div className="flex flex-col w-full">
        <HeaderRow variant={variant} productUrlParam={productUrlParam} />
        <div className="mx-2 h-[1px] bg-border" />
        <div className="w-full px-4 pt-2 pb-4 text-sm  justify-start text-left flex flex-col text-foreground-secondary">
          {variant === "nettle" && (
            <>
              <BenefitItem icon={Shield} benefit="Regulated medical device" />
              <BenefitItem icon={Home} benefit="Easy to use at home" />
              <BenefitItem
                icon={Star}
                benefit="Reduces and relieves menstrual symptoms"
              />
            </>
          )}
          {variant === "membershipOnly" && (
            <>
              <BenefitItem
                icon={Smartphone}
                benefit="Personalized cycle tracking and app access"
              />
              <BenefitItem
                icon={BookOpen}
                benefit="Education, meditation and breathwork resources"
              />
              <BenefitItem
                icon={BrainCog}
                benefit="Boost neuroplasticity throughout the cycle"
              />
            </>
          )}
        </div>
      </div>
      <img
        alt={variant}
        className="w-[25%] min-w-[150px] sm:min-w-[100px] h-full object-cover rounded-br-2xl rounded-tr-2xl lg:hidden block"
        src={variant === "nettle" ? stage1Nettle : stage1AppOnly}
      />
      <div className="absolute bg top-2 right-2">
        <CheckIndicator productUrlParam={productUrlParam} variant={variant} />
      </div>
    </button>
  );
}

export function BenefitItem({
  benefit,
  icon: Icon,
  className,
}: {
  benefit: string;
  icon: LucideIcon;
  className?: string;
}) {
  return (
    <div className="flex flex-row gap-1 items-start mb-2">
      <Icon className="w-4 h-4 stroke-2 text-brand-primary shrink-0 mr-0 relative top-[0px]" />
      <span className={cn("text-xs text-foreground-primary", className)}>
        {benefit}
      </span>
    </div>
  );
}

function HeaderRow({
  variant,
  productUrlParam,
}: {
  variant: ProductTypes;
  productUrlParam: ProductTypes;
}) {
  return (
    <div className="w-full  h-24px flex flex-row items-center justify-between ">
      <div className="mt-2 px-4 py-2 flex flex-col text-left">
        <div className="flex flex-row  justify-center gap-2 items-center  mb-1">
          {variant === "nettle" && (
            <>
              <NettleLogo width={60} />
              <div>
                <p
                  className="bg-blue-100 px-2 py-1 rounded-2xl"
                  style={{ fontSize: 8 }}
                >
                  Medical Device & Membership
                </p>
              </div>
            </>
          )}
          {variant === "membershipOnly" && (
            <>
              <SamphireLogo width={100} />
              <div>
                <p
                  className=" bg-blue-100 px-2 py-1 rounded-2xl"
                  style={{ fontSize: 8 }}
                >
                  Membership Only
                </p>
              </div>
            </>
            // <p className=" text-md font-bold text-foreground-primary flex flex-row items-center">
            //   Samphire Membership
            // </p>
          )}
        </div>
        {/* <p className=" text-xs text-foreground-secondary  mb-0">
          {`Starting at ${variant === "nettle" ? "£59 / month" : "£2.49 / month"}`}
        </p> */}
      </div>
    </div>
  );
}

function CheckIndicator({
  productUrlParam,
  variant,
}: {
  productUrlParam: ProductTypes;
  variant: ProductTypes;
}) {
  return (
    <div
      className={cn(
        "flex shrink-0  flex-row items-center justify-center text-surface-2 border-white lg:border-border border-2 lg:border-1 rounded-full w-6 h-6",
        productUrlParam === variant &&
          "bg-brand-primary border-1 lg:border-brand-primary-darker border-brand-primary-darker",
      )}
    >
      <CheckIcon
        className={cn(
          "w-3 h-3 opacity-0 shrink-0",
          productUrlParam === variant && "opacity-100 text-white",
        )}
      />
    </div>
  );
}
